import { Link, useLocation } from "react-router-dom";

import type { PlaceOrderResult } from "@/lib/checkout";
import { formatXOF } from "@/lib/format";

/**
 * Page de confirmation (VVS_MODELE_INTERFACE.md §9.1). Pas de nouvel appel
 * API : comme l'original, se contente d'afficher les champs utiles de la
 * réponse `POST /checkout/orders` (`GET /orders/:orderNumber` appartient au
 * module `order/`, hors périmètre de cette page).
 *
 * Transmission du résultat — écart assumé par rapport à
 * apps/web/app/(shop)/commande/confirmation/page.tsx : l'original recevait
 * ces champs en QUERY STRING, parce que `placeOrderAction` était une Server
 * Action qui terminait par `redirect()` (seul mécanisme disponible pour lui
 * faire porter des données vers la page suivante). Ici, `placeOrderAction`
 * n'existe plus comme redirection serveur : c'est un `useMutation`
 * (`usePlaceOrder`, src/lib/checkout.ts) appelé depuis `CheckoutForm`, dans le
 * même arbre React que `CommandePage`, qui appelle ensuite lui-même
 * `navigate("/commande/confirmation", { state })`. `location.state` (React
 * Router) a donc été préféré à une reconstruction manuelle de query string :
 *  - pas de sérialisation/parsing à la main de valeurs typées (montants en
 *    centimes, `expectedWithinHours` numérique…) ;
 *  - n'expose pas le détail chiffré de la commande (total, nom du
 *    destinataire…) dans l'URL ni l'historique du navigateur.
 * Contrepartie assumée, réplique du comportement d'origine sans les
 * paramètres de query correspondants : cette page ne fonctionne qu'en
 * arrivant DEPUIS `CommandePage` (juste après validation) — un accès direct
 * par URL ou un rafraîchissement de page affiche "Aucune commande à
 * afficher", exactement comme l'original sans `?orderNumber=…`.
 */
export function CommandeConfirmationPage() {
  const location = useLocation();
  const state = (location.state as PlaceOrderResult | null) ?? null;

  if (!state?.order) {
    return (
      <div className="container-vvs py-16 text-center">
        <p className="text-sm text-muted">Aucune commande à afficher.</p>
        <Link to="/" className="mt-4 inline-block text-sm font-medium text-navy hover:text-gold">
          Retour à l&apos;accueil
        </Link>
      </div>
    );
  }

  const { order, recipientName, districtName, shippingMethodLabel, itemCount } = state;
  const expectedWithinHours = order.nextAction.expectedWithinHours ?? null;

  return (
    <div className="container-vvs flex flex-col gap-6 py-10">
      <div className="flex flex-col items-center gap-2 border border-line bg-white px-6 py-10 text-center">
        <p aria-hidden="true" className="text-3xl">
          ✔
        </p>
        <h1 className="font-display text-2xl font-semibold text-ink">Merci, votre commande est enregistrée</h1>
        <p className="text-sm font-medium text-ink">N° {order.orderNumber}</p>

        <p className="mt-4 max-w-md border border-line bg-cream2 px-4 py-3 text-sm text-ink">
          <span aria-hidden="true">ⓘ</span> {order.message}
          {expectedWithinHours ? ` Sous ${expectedWithinHours} h.` : null}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 border border-line bg-white p-4 sm:grid-cols-3">
        <div>
          <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">Récapitulatif</h2>
          {itemCount ? (
            <p className="mt-1 text-sm text-ink">
              {itemCount} article{itemCount > 1 ? "s" : ""}
            </p>
          ) : null}
          <p className="text-sm text-ink">Sous-total {formatXOF(order.totals.subtotal.amountMinor)}</p>
          <p className="text-sm text-ink">Livraison {formatXOF(order.totals.shipping.amountMinor)}</p>
          <p className="mt-1 text-sm font-semibold text-ink">Total {formatXOF(order.totals.grandTotal.amountMinor)}</p>
        </div>
        <div>
          <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">Livraison</h2>
          {recipientName ? <p className="mt-1 text-sm text-ink">{recipientName}</p> : null}
          {districtName ? <p className="text-sm text-ink">{districtName}</p> : null}
          {shippingMethodLabel ? <p className="text-sm text-ink">{shippingMethodLabel}</p> : null}
        </div>
        <div>
          <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">Paiement</h2>
          <p className="mt-1 text-sm text-ink">À la livraison</p>
          <p className="text-sm text-ink">{formatXOF(order.totals.grandTotal.amountMinor)} à préparer</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <Link
          to="/"
          className="inline-flex items-center justify-center border border-gold bg-gold px-6 py-3 text-sm font-medium uppercase tracking-wide text-navy hover:border-gold2 hover:bg-gold2"
        >
          Continuer mes achats
        </Link>
        <Link
          to="/connexion"
          className="inline-flex items-center justify-center border border-navy px-6 py-3 text-sm font-medium uppercase tracking-wide text-navy hover:bg-navy hover:text-cream"
        >
          Créer mon compte pour suivre mes commandes
        </Link>
      </div>
    </div>
  );
}
